class IcsSupplierAvailability {
  constructor(root) {
    this.root = root;
    this.forms = Array.from(root.querySelectorAll('[data-availability-search]'));
    this.inputs = Array.from(root.querySelectorAll('[data-availability-input]'));
    this.hints = Array.from(root.querySelectorAll('[data-search-hint]'));
    this.results = root.querySelector('[data-availability-results]');
    this.resultsBody = root.querySelector('[data-results-body]');
    this.resultSummary = root.querySelector('[data-result-summary]');
    this.checkedTime = root.querySelector('[data-checked-time]');
    this.retryButton = root.querySelector('[data-availability-retry]');
    this.errorMessage = root.querySelector('[data-error-message]');
    this.panels = Array.from(root.querySelectorAll('[data-state-panel]'));
    this.minimumQueryLength = Number(root.dataset.minQuery || 3);
    this.lastQuery = '';
    this.controller = null;

    this.hints.forEach((hint) => {
      hint.dataset.defaultText = hint.textContent.trim();
    });

    this.forms.forEach((form) => {
      form.addEventListener('submit', (event) => this.handleSubmit(event));
    });

    if (this.retryButton) {
      this.retryButton.addEventListener('click', () => {
        if (this.lastQuery) this.lookup(this.lastQuery);
      });
    }

    this.applyInitialState();
  }

  applyInitialState() {
    const previewState = this.root.dataset.previewState || 'initial';
    const isDesignMode = this.root.dataset.designMode === 'true';

    if (isDesignMode) {
      this.showState(previewState);
      if (previewState === 'results') {
        const sampleCount = this.resultsBody
          ? this.resultsBody.querySelectorAll('[data-sample-result]').length
          : 0;
        this.updateSummary(sampleCount, 'Theme Editor example');
      }
      return;
    }

    const query = new URLSearchParams(window.location.search).get('q');
    if (query && query.trim().length >= this.minimumQueryLength) {
      this.syncInputs(query.trim());
      this.lookup(query.trim());
      return;
    }

    this.showState('initial');
  }

  handleSubmit(event) {
    event.preventDefault();
    const input = event.currentTarget.querySelector('[data-availability-input]');
    const query = input ? input.value.trim() : '';

    if (query.length < this.minimumQueryLength) {
      this.setHintError(`Enter at least ${this.minimumQueryLength} characters.`);
      if (input) input.focus();
      return;
    }

    this.restoreHints();
    this.syncInputs(query);
    this.updateUrl(query);
    this.lookup(query);
  }

  syncInputs(query) {
    this.inputs.forEach((input) => {
      input.value = query;
    });
  }

  updateUrl(query) {
    const url = new URL(window.location.href);
    url.searchParams.set('q', query);
    window.history.replaceState({}, '', url);
  }

  setHintError(message) {
    this.hints.forEach((hint) => {
      hint.textContent = message;
      hint.classList.add('is-error');
    });
  }

  restoreHints() {
    this.hints.forEach((hint) => {
      hint.textContent = hint.dataset.defaultText || '';
      hint.classList.remove('is-error');
    });
  }

  showState(name) {
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.statePanel !== name;
    });

    if (this.results) {
      this.results.setAttribute('aria-busy', name === 'loading' ? 'true' : 'false');
    }
  }

  async lookup(query) {
    this.lastQuery = query;
    this.restoreHints();

    if (!window.navigator.onLine) {
      this.showError(this.root.dataset.offlineLabel);
      return;
    }

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this.showState('loading');
    this.updateSummary(0, this.root.dataset.loadingLabel);

    const timeout = window.setTimeout(() => this.controller.abort(), 9000);

    try {
      const endpoint = new URL(this.root.dataset.endpoint, window.location.origin);
      endpoint.searchParams.set('q', query);
      endpoint.searchParams.set('resources[type]', 'product');
      endpoint.searchParams.set('resources[limit]', '8');
      endpoint.searchParams.set('resources[options][unavailable_products]', 'show');
      endpoint.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,variants.sku,vendor',
      );
      endpoint.searchParams.set('options[prefix]', 'last');

      const response = await window.fetch(endpoint.toString(), {
        headers: { Accept: 'application/json' },
        signal: this.controller.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = await response.json();
      const products = payload?.resources?.results?.products;
      const matches = Array.isArray(products) ? products : [];

      if (matches.length === 0) {
        this.clearResults();
        this.showState('empty');
        this.updateSummary(0, 'No published catalog match');
        this.updateCheckedTime();
        return;
      }

      this.renderResults(matches);
      this.showState('results');
      this.updateSummary(matches.length);
      this.updateCheckedTime();
    } catch (error) {
      if (error.name === 'AbortError') {
        this.showError(this.root.dataset.errorLabel);
      } else {
        this.showError(this.root.dataset.errorLabel);
      }
    } finally {
      window.clearTimeout(timeout);
    }
  }

  clearResults() {
    if (!this.resultsBody) return;
    this.resultsBody.replaceChildren();
  }

  renderResults(products) {
    if (!this.resultsBody) return;
    const fragment = document.createDocumentFragment();

    products.forEach((product) => {
      fragment.appendChild(this.createResultRow(product));
    });

    this.resultsBody.replaceChildren(fragment);
  }

  createResultRow(product) {
    const row = document.createElement('tr');
    const firstVariant = Array.isArray(product.variants) ? product.variants[0] : null;
    const partNumber = firstVariant?.sku || product.title || 'Part number pending';
    const productType = product.type || product.product_type || 'Published catalog record';
    const manufacturer = product.vendor || 'Manufacturer pending';

    const partCell = this.createCell('Part');
    const partTitle = document.createElement('strong');
    partTitle.textContent = partNumber;
    const partType = document.createElement('span');
    partType.textContent = productType;
    partCell.append(partTitle, partType);

    const manufacturerCell = this.createCell('Manufacturer');
    manufacturerCell.textContent = manufacturer;

    const statusCell = this.createCell('Catalog status');
    const status = document.createElement('span');
    status.className = 'ics-supplier__verification-label';
    status.textContent = 'Verification required';
    statusCell.appendChild(status);

    const termsCell = this.createCell('Commercial terms');
    const termsTitle = document.createElement('strong');
    termsTitle.textContent = 'Confirmed in quote';
    const termsText = document.createElement('span');
    termsText.textContent = 'Price, MOQ, package and lead time';
    termsCell.append(termsTitle, termsText);

    const actionCell = this.createCell('Action');
    const quoteLink = document.createElement('a');
    quoteLink.href = this.buildQuoteUrl(partNumber, manufacturer);
    quoteLink.textContent = 'Request quote';
    actionCell.appendChild(quoteLink);

    row.append(partCell, manufacturerCell, statusCell, termsCell, actionCell);
    return row;
  }

  createCell(label) {
    const cell = document.createElement('td');
    cell.dataset.label = label;
    return cell;
  }

  buildQuoteUrl(partNumber, manufacturer) {
    const quoteUrl = new URL(this.root.dataset.rfqBase, window.location.origin);
    quoteUrl.searchParams.set('part', partNumber);
    quoteUrl.searchParams.set('manufacturer', manufacturer);
    quoteUrl.searchParams.set('source', 'supplier-availability');
    return `${quoteUrl.pathname}${quoteUrl.search}`;
  }

  updateSummary(count, override) {
    if (!this.resultSummary) return;

    if (override) {
      this.resultSummary.textContent = override;
      return;
    }

    const label = count === 1 ? this.root.dataset.resultLabel : this.root.dataset.resultsLabel;
    this.resultSummary.textContent = `${count} ${label}`;
  }

  updateCheckedTime() {
    if (!this.checkedTime) return;
    const language = document.documentElement.lang || 'en';
    const formatted = new Intl.DateTimeFormat(language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date());
    this.checkedTime.textContent = `${this.root.dataset.checkedLabel}: ${formatted}`;
    this.checkedTime.dateTime = new Date().toISOString();
  }

  showError(message) {
    if (this.errorMessage && message) this.errorMessage.textContent = message;
    this.showState('error');
    this.updateSummary(0, 'Lookup unavailable');
    this.updateCheckedTime();
  }
}

function initializeIcsSupplierAvailability(scope = document) {
  scope.querySelectorAll('[data-ics-supplier-availability]').forEach((root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    new IcsSupplierAvailability(root);
  });
}

initializeIcsSupplierAvailability();

document.addEventListener('shopify:section:load', (event) => {
  initializeIcsSupplierAvailability(event.target);
});
